import * as XLSX from 'xlsx'

/**
 * Converts an Excel file (.xlsx, .xls) to CSV format
 * @param {File} file - The Excel file to convert
 * @param {Object} metadata - Optional metadata with subid and datasetId for proper naming
 * @returns {Promise<File>} - A new File object containing CSV data
 */
export async function convertExcelToCsv(file, metadata = null) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = function(e) {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        
        // Convert to CSV with proper formatting
        const csvString = XLSX.utils.sheet_to_csv(worksheet, {
          blankrows: false, // Skip empty rows
          defval: '', // Default value for empty cells
          raw: false // Convert numbers to strings
        })
        
        console.log(`📄 CSV content preview (first 500 chars):`, csvString.substring(0, 500))
        
        // Create a new File object with CSV content
        const csvBlob = new Blob([csvString], { type: 'text/csv' })
        
        // Generate proper filename based on metadata or original name
        let csvFilename
        if (metadata && metadata.subid && metadata.datasetId) {
          // Use the proper format expected by backend: subid_datasetid.csv
          csvFilename = `${metadata.subid}_${metadata.datasetId}.csv`
          console.log(`🏷️ Generated proper filename: ${csvFilename}`)
        } else {
          // Fallback to original name with .csv extension
          const originalName = file.name
          const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '')
          csvFilename = `${nameWithoutExt}.csv`
        }
        
        const csvFile = new File([csvBlob], csvFilename, {
          type: 'text/csv',
          lastModified: Date.now()
        })
        
        console.log(`✅ Converted ${file.name} to ${csvFilename}`)
        resolve(csvFile)
        
      } catch (error) {
        console.error('❌ Error converting Excel to CSV:', error)
        reject(error)
      }
    }
    
    reader.onerror = function() {
      reject(new Error('Failed to read the Excel file'))
    }
    
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Checks if a file is an Excel file
 * @param {File} file - The file to check
 * @returns {boolean} - True if the file is an Excel file
 */
export function isExcelFile(file) {
  const excelExtensions = ['.xlsx', '.xls']
  const fileName = file.name.toLowerCase()
  return excelExtensions.some(ext => fileName.endsWith(ext))
}

/**
 * Processes a file, converting Excel files to CSV if needed
 * @param {File} file - The file to process
 * @param {Object} metadata - Optional metadata with subid and datasetId for proper naming
 * @returns {Promise<File>} - The processed file (CSV or original)
 */
export async function processFileForUpload(file, metadata = null) {
  if (isExcelFile(file)) {
    console.log(`🔄 Converting Excel file: ${file.name}`)
    return await convertExcelToCsv(file, metadata)
  } else {
    console.log(`✅ File is already CSV: ${file.name}`)
    return file
  }
}
